import React, {useEffect, useState} from "react";
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    message,
    Space,
    Popconfirm
} from "antd";
import EventDatesFormList from "../../components/EventDatesFormList";
import type {DefaultOptionType} from "antd/es/select";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import api from "../../api";
import Title from "antd/es/typography/Title";
import {exportToExcel} from "../../utils/exportToExcel";
import type {AxiosResponse} from "axios";

const {RangePicker} = DatePicker;
const {Option} = Select;
dayjs.extend(utc);

const EntertainmentPage: React.FC = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [requests, setRequests] = useState<any[]>([]);
    const [editingRequest, setEditingRequest] = useState<any | null>(null);
    const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
    const [loadingRequests, setLoadingRequests] = useState<boolean>(false);
    const [formVisible, setFormVisible] = useState(false);
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState<any[]>([]);
    const priorityOptions = ["low", "medium", "high", "very_high"];
    const [eventDateFilter, setEventDateFilter] =
        useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [eventsLookup, setEventsLookup] = useState<any>({});
    const [eventModalVisible, setEventModalVisible] = useState(false);
    const [editingEvent, setEditingEvent] = useState<any | null>(null);
    const [totalEvents, setTotalEvents] = useState(0);
    const [pagination_events, setPagination_events] = useState({
        current: 1,
        pageSize: 10,
    });
    const [totaltickets, setTotalTickets] = useState(0);
    const [pagination_tickets, setPagination_tickets] = useState({
        current: 1,
        pageSize: 10,
    });
    const [eventForm] = Form.useForm();
    const [dateFilter, setDateFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const selectedEvent = Form.useWatch("event", form);
    const [eventNameFilter, setEventNameFilter] = useState<string | null>(null);
    const [filteredRequests, setFilteredRequests] = useState<any[]>([]);

    const PRIORITY_ORDER: Record<string, number> = {
        very_high: 4,
        high: 3,
        medium: 2,
        low: 1,
    };

    useEffect(() => {
        fetchEvents(pagination_events.current, pagination_events.pageSize);
    }, [pagination_events.current, pagination_events.pageSize]);

    useEffect(() => {
        fetchRequests();
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, []);

    // helper: earliest upcoming date for an event (today or future)
    const getNextEventDate = (e: any) => {
        const today = dayjs().startOf("day");
        const ds = (e.event_dates || [])
            .map((ed: any) => dayjs(ed?.date ?? ed))
            .filter((d: dayjs.Dayjs) => d.isSame(today, "day") || d.isAfter(today, "day"))
            .sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => a.valueOf() - b.valueOf());
        return ds[0] || null;
    };


    const fetchEvents = async (page = 1, pageSize = 10) => {
        setLoadingEvents(true);
        try {
            const res = await api.get("/cc/events/", {
                params: {
                    page,
                    page_size: pageSize,
                },
            });

            const results = res.data.results || res.data;
            setTotalEvents(res.data.count || 0);

            const today = new Date();

            const filtered = results.filter((event: any) => {
                const dates = event.event_dates || [];
                return dates.some((d: any) => new Date(d.date) >= today);
            });

            const sortedEvents = filtered.sort((a: any, b: any) => {
                const pa = PRIORITY_ORDER[a.priority] ?? 0;
                const pb = PRIORITY_ORDER[b.priority] ?? 0;
                if (pb !== pa) return pb - pa; // higher priority first

                // tie-breaker 1: earlier upcoming date first
                const na = getNextEventDate(a);
                const nb = getNextEventDate(b);
                if (na && nb && na.valueOf() !== nb.valueOf()) {
                    return na.valueOf() - nb.valueOf();
                }

                // tie-breaker 2: newest created first
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });

            setEvents(sortedEvents);

            const lookup = Object.fromEntries(
                sortedEvents.map((e: any) => [e.id, e])
            );
            setEventsLookup(lookup);
        } catch (err) {
            console.error("Failed to fetch events", err);
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchRequests = async (): Promise<void> => {
        setLoadingRequests(true);
        try {
            // either single call with all=true …
            // const { data } = await api.get("/cc/ticket-requests/", { params: { all: true }});
            // setRequests(data.results || data);

            // …or DRF paging loop:
            let url = "/cc/ticket-requests/";
            let all: any[] = [];
            while (true) {
                const {data} = await api.get(url);
                const batch = data.results || data;
                all = all.concat(batch);
                if (data.next) {
                    url = data.next; // has page params already
                } else break;
            }
            setRequests(all);
        } catch (e) {
            console.error("Failed to fetch ticket requests", e);
        } finally {
            setLoadingRequests(false);
        }
    };

    // Parse request.event_dates which may be an array of objects OR a JSON string
    const getRequestEventDayjsDates = (req: any): dayjs.Dayjs[] => {
        let dates = req.event_dates;
        if (typeof dates === "string") {
            try {
                dates = JSON.parse(dates);
            } catch {
                dates = [];
            }
        }
        if (!Array.isArray(dates)) return [];

        return dates
            .map((ed: any) => {
                const dateStr = typeof ed === "string" ? ed : ed?.date;
                if (!dateStr) return null;

                // try strict first
                let d = dayjs(dateStr, "YYYY-MM-DD", true);
                if (!d.isValid()) d = dayjs(dateStr);    // fallback to loose parse
                return d.isValid() ? d.startOf("day") : null;
            })
            .filter(Boolean) as dayjs.Dayjs[];
    };

    const toDay = (d?: dayjs.Dayjs | string) =>
        d ? dayjs.isDayjs(d) ? d.startOf("day") : dayjs(d).startOf("day") : null;

    // Earliest date that is today or in the future; if none, return null
    const getNextUpcomingDate = (req: any): dayjs.Dayjs | null => {
        const today = dayjs().startOf("day");
        const ds = getRequestEventDayjsDates(req).sort((a, b) => a.valueOf() - b.valueOf());
        for (const d of ds) {
            if (d.isSame(today, "day") || d.isAfter(today, "day")) return d;
        }
        return null;
    };

    // Latest past event date (for ordering past requests)
    const getLatestPastDate = (req: any): dayjs.Dayjs | null => {
        const today = dayjs().startOf("day");
        const ds = getRequestEventDayjsDates(req).filter(d => d.isBefore(today, "day"));
        if (!ds.length) return null;
        return ds.sort((a, b) => b.valueOf() - a.valueOf())[0];
    };

    const processedTicketRequests = React.useMemo(() => {
        return requests.map(r => {
            const nextDate = getNextUpcomingDate(r);        // today/future or null
            const latestPast = getLatestPastDate(r);        // latest past date or null
            const allDates = getRequestEventDayjsDates(r);  // for filtering
            return {...r, __nextDate: nextDate, __latestPast: latestPast, __allDates: allDates};
        });
    }, [requests]);

    const filteredSortedTicketRequests = React.useMemo(() => {
        const start = eventDateFilter?.[0]?.startOf("day");
        const end = eventDateFilter?.[1]?.endOf("day");

        // 1) FILTER by event name & event date range (inclusive)
        const filtered = processedTicketRequests.filter((r: any) => {
            if (eventNameFilter && r.event_name !== eventNameFilter) return false;

            if (start && end) {
                if (!r.__allDates?.length) return false;
                const inRange = r.__allDates.some((d: dayjs.Dayjs) =>
                    d.isSame(start, "day") || d.isSame(end, "day") ||
                    (d.isAfter(start, "day") && d.isBefore(end, "day"))
                );
                if (!inRange) return false;
            }
            return true;
        });

        // 2) SORT order:
        //    a) Upcoming bucket (has __nextDate): sort ascending by __nextDate (next first)
        //    b) Past bucket (no __nextDate, but has __latestPast): sort descending by __latestPast (most recent past first)
        //    c) No-date bucket: sent to bottom, stable by created_at desc just in case
        const upcoming = filtered
            .filter(r => !!r.__nextDate)
            .sort((a, b) => a.__nextDate.valueOf() - b.__nextDate.valueOf());

        const past = filtered
            .filter(r => !r.__nextDate && !!r.__latestPast)
            .sort((a, b) => b.__latestPast.valueOf() - a.__latestPast.valueOf());

        const noDate = filtered
            .filter(r => !r.__nextDate && !r.__latestPast)
            .sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf());

        return [...upcoming, ...past, ...noDate];
    }, [processedTicketRequests, eventNameFilter, eventDateFilter]);

// 3) PAGINATION (client-side after filtering+sorting)
    const pagedTicketRequests = React.useMemo(() => {
        const {current, pageSize} = pagination_tickets;
        const startIdx = (current - 1) * pageSize;
        const endIdx = startIdx + pageSize;
        return filteredSortedTicketRequests.slice(startIdx, endIdx);
    }, [filteredSortedTicketRequests, pagination_tickets]);

    const fetchCustomers = async (): Promise<void> => {
        try {
            let url: string | null = "/cc/customers/?page=1&page_size=500";
            let all: any[] = [];

            while (url) {
                const res: AxiosResponse<any> = await api.get(url);

                const batch = res.data?.results ?? res.data;

                all = all.concat(Array.isArray(batch) ? batch : []);

                url = res.data?.next ?? null;
            }

            setCustomers(all);
        } catch (err) {
            console.error("Failed to load customers", err);
            setCustomers([]);
        }
    };

    const handleDeleteRequest = async (id: number) => {
        await api.delete(`/cc/ticket-requests/${id}/`);
        message.success("Request deleted");
        fetchRequests();
    };

    const handleEditEvent = (eventData: any) => {
        setEditingEvent(eventData);

        const formattedDates = (eventData.event_dates || []).map((ed: any) => {
            const date = dayjs(ed.date);
            const isAllDay = ed.all_day;

            return {
                date,
                start_time: isAllDay
                    ? dayjs().hour(0).minute(0)
                    : dayjs(ed.start_time),
                end_time: isAllDay
                    ? dayjs().hour(23).minute(59)
                    : dayjs(ed.end_time),
                all_day: isAllDay,
            };
        });


        eventForm.setFieldsValue({
            name: eventData.name,
            priority: eventData.priority,
            event_dates: formattedDates,
        });

        setEventModalVisible(true);
    };


    const handleSubmit = async (values: any) => {
        const isEventForm = !!values.event_dates;

        if (isEventForm) {
            const cleanedEventDates = values.event_dates.map((ed: any) => {
                const isAllDay = ed.all_day;

                return {
                    date: dayjs(ed.date).format("YYYY-MM-DD"),
                    start_time: isAllDay ? "00:00" : dayjs(ed.start_time).format("HH:mm"),
                    end_time: isAllDay ? "23:59" : dayjs(ed.end_time).format("HH:mm"),
                    all_day: isAllDay,
                };
            });

            const payload = {
                name: values.name,
                event_dates: cleanedEventDates,
                priority: values.priority,
            };

            try {
                if (editingEvent) {
                    await api.patch(`/cc/events/${editingEvent.id}/`, payload);
                    message.success("Event updated");
                } else {
                    await api.post("/cc/events/", payload);
                    message.success("Event created");
                }

                eventForm.resetFields();
                setEventModalVisible(false);
                setEditingEvent(null);
                fetchEvents();
            } catch {
                message.error(editingEvent ? "Failed to update event" : "Failed to create event");
            }

        } else {
            // Ticket request form logic
            const payload = {
                customer: values.customer,
                event: values.event,
                preferred_dates: values.preferred_dates || [],
                number_of_tickets: values.number_of_tickets,
                special_requests: values.special_requests,
            };

            try {
                if (editingRequest) {
                    await api.patch(`/cc/ticket-requests/${editingRequest.id}/`, payload);
                    message.success("Request updated");
                } else {
                    await api.post("/cc/ticket-requests/", payload);
                    message.success("Ticket request submitted");
                }

                form.resetFields();
                setEditingRequest(null);
                setFormVisible(false);
                fetchRequests();
            } catch {
                message.error(editingRequest ? "Failed to update request" : "Failed to submit request");
            }
        }
    };


    const filteredEvents = dateFilter
        ? events.filter((e) =>
            e.event_dates.some((ed: any) => {
                const d = dayjs(ed?.date ?? ed);
                return d.isSameOrAfter(dateFilter[0].startOf("day"), "day") &&
                    d.isSameOrBefore(dateFilter[1].endOf("day"), "day");
            })
        )
        : events;

    const pagedEvents = React.useMemo(() => {
        const {current, pageSize} = pagination_events;
        const start = (current - 1) * pageSize;
        return filteredEvents.slice(start, start + pageSize);
    }, [filteredEvents, pagination_events]);

// if filtering reduces total pages, keep current in range
    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredEvents.length / pagination_events.pageSize));
        if (pagination_events.current > maxPage) {
            setPagination_events(s => ({...s, current: 1}));
        }
    }, [filteredEvents.length, pagination_events.pageSize]);

    const handleExport = () => {
        const source = filteredSortedTicketRequests;

        const formatted = source.map((r) => {
            // Normalize to array
            let datesArray: any[] = [];
            try {
                datesArray = typeof r.event_dates === "string" ? JSON.parse(r.event_dates) : (r.event_dates || []);
            } catch {
                datesArray = [];
            }

            // Take date-only, unique, sorted
            const dateStrings = Array.from(
                new Set(
                    datesArray
                        .map((d: any) => dayjs(d?.date ?? d).format("YYYY-MM-DD"))
                        .filter(Boolean)
                )
            ).sort();

            const eventDatesFormatted = dateStrings.join(", ");   // <- no times

            const preferredDatesFormatted = Array.isArray(r.preferred_dates)
                ? r.preferred_dates
                    .map((d: string) => dayjs(d).format("YYYY-MM-DD"))
                    .join(", ")
                : "";

            return {
                MMID: r.mmid,
                "Customer Name": r.customer_name,
                "Event Name": r.event_name,
                "Event Dates": eventDatesFormatted,          // <- dates only
                "Preferred Dates": preferredDatesFormatted,
                "Number of Tickets": r.number_of_tickets,
                "Special Requests": r.special_requests,
                "Request Date": dayjs(r.created_at).format("YYYY-MM-DD"),
            };
        });

        exportToExcel(
            formatted,
            `CasinoClub_Ticket_Requests_${dayjs().format("YYYY-MM-DD")}.xlsx`
        );
    };

    return (
        <div>
            <Card
                title={<Title level={2} style={{margin: 0}}>Upcoming Events</Title>}
                extra={
                    <Space wrap>
                        <RangePicker
                            onChange={(val) => {
                                if (val && val[0] && val[1]) {
                                    setDateFilter([val[0], val[1]]);
                                } else {
                                    setDateFilter(null);
                                }
                            }}
                        />
                        <Button
                            type="primary"
                            onClick={() => {
                                setEditingEvent(null);
                                setEventModalVisible(true);
                            }}
                        >
                            New Event
                        </Button>
                    </Space>
                }
                style={{marginBottom: 24}}
            >
                <Table
                    scroll={{x: "max-content"}}
                    loading={loadingEvents}
                    dataSource={pagedEvents}
                    rowKey="id"
                    pagination={{
                        current: pagination_events.current,
                        pageSize: pagination_events.pageSize,
                        total: filteredEvents.length,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination_events({current: page, pageSize});
                        },
                    }}
                    columns={[
                        {
                            title: "Event Name",
                            dataIndex: "name",
                        },
                        {
                            title: "Event Dates",
                            dataIndex: "event_dates",
                            render: (
                                dates: { date: string; start_time: string; end_time: string; all_day: boolean }[]
                            ) => {
                                const allDayDates = dates.filter((d) => d.all_day).map((d) => d.date);
                                const timedDates = dates.filter((d) => !d.all_day);

                                const formatRange = (dateStrings: string[]) => {
                                    const sorted = dateStrings.map(dayjs).sort((a, b) => a.diff(b));
                                    return sorted.length <= 1
                                        ? sorted[0]?.format("YYYY-MM-DD")
                                        : `${sorted[0].format("YYYY-MM-DD")} - ${sorted[sorted.length - 1].format("YYYY-MM-DD")}`;
                                };

                                const allDayDisplay = allDayDates.length
                                    ? `${formatRange(allDayDates)} (All Day)`
                                    : "";

                                const timedDisplay = timedDates
                                    .map(
                                        (d) =>
                                            `${dayjs(d.date).format("YYYY-MM-DD")} (${dayjs(d.start_time).format("HH:mm")} - ${dayjs(d.end_time).format("HH:mm")})`
                                    )
                                    .join(", ");

                                return [allDayDisplay, timedDisplay].filter(Boolean).join(", ");
                            },
                        },
                        {
                            title: "Priority",
                            dataIndex: "priority",
                            render: (text: string) => text.replace("_", " ").toUpperCase(),
                        },
                        {
                            title: "Actions",
                            key: "actions",
                            render: (_: any, record: any) => (
                                <Space>
                                    <Button
                                        size="small"
                                        onClick={() => handleEditEvent(record)}
                                    >
                                        Edit
                                    </Button>
                                    <Popconfirm
                                        title="Delete this event?"
                                        onConfirm={async () => {
                                            try {
                                                await api.delete(`/cc/events/${record.id}/`);
                                                message.success("Event deleted");
                                                fetchEvents();
                                            } catch {
                                                message.error("Delete failed");
                                            }
                                        }}
                                    >
                                        <Button size="small" danger>
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>
            <Card
                title={
                    <div style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8}}>
                        <Title level={2} style={{margin: 0}}>Ticket Requests</Title>
                        <Space wrap>
                            <RangePicker
                                onChange={(val) => {
                                    if (val && val[0] && val[1]) {
                                        setEventDateFilter([val[0], val[1]]);
                                    } else {
                                        setEventDateFilter(null);
                                    }
                                    // reset to page 1 whenever filtering changes
                                    setPagination_tickets(prev => ({...prev, current: 1}));
                                }}
                            />
                            <Select
                                allowClear
                                placeholder="Filter by Event"
                                style={{minWidth: 200}}
                                value={eventNameFilter}
                                onChange={(value) => {
                                    setEventNameFilter(value || null);
                                    setPagination_tickets(prev => ({...prev, current: 1}));
                                }}
                            >
                                {events.map(event => (
                                    <Select.Option key={event.id} value={event.name}>
                                        {event.name}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                onClick={() => {
                                    handleExport()
                                }}
                            >
                                Export Excel
                            </Button>
                            <Button type="primary" onClick={() => setFormVisible(true)}>
                                Request Tickets
                            </Button>
                        </Space>
                    </div>
                }
                style={{marginBottom: 24}}
            >
                <Table
                    scroll={{x: "max-content"}}
                    loading={loadingRequests}
                    dataSource={pagedTicketRequests}
                    rowKey="id"
                    pagination={{
                        current: pagination_tickets.current,
                        pageSize: pagination_tickets.pageSize,
                        total: filteredSortedTicketRequests.length, // IMPORTANT: total of filtered+sorted list
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination_tickets({current: page, pageSize});
                        },
                    }}
                    columns={[
                        {title: "Customer", dataIndex: "customer_name"},
                        {title: "MMID", dataIndex: "mmid"},
                        {
                            title: "Request Date",
                            dataIndex: "created_at",
                            render: (val) => dayjs(val).format("YYYY-MM-DD"),
                        },
                        {title: "Event", dataIndex: "event_name"},
                        {
                            title: "Event Dates",
                            dataIndex: "event_dates",
                            render: (dates: { date: string; start_time: string; end_time: string; all_day: boolean }[]) => {
                                const allDayDates = dates.filter((d) => d.all_day).map((d) => d.date);
                                const timedDates = dates.filter((d) => !d.all_day);

                                const formatRange = (ds: string[]) => {
                                    const sorted = ds.map(dayjs).sort((a, b) => a.diff(b));
                                    return sorted.length <= 1
                                        ? sorted[0]?.format("YYYY-MM-DD")
                                        : `${sorted[0].format("YYYY-MM-DD")} - ${sorted[sorted.length - 1].format("YYYY-MM-DD")}`;
                                };

                                const allDayDisplay = allDayDates.length
                                    ? `${formatRange(allDayDates)} (All Day)`
                                    : "";

                                const timedDisplay = timedDates
                                    .map((d: { date: string; start_time: string; end_time: string }) =>
                                        `${dayjs(d.date).format("YYYY-MM-DD")} (${dayjs(d.start_time).format("HH:mm")} - ${dayjs(d.end_time).format("HH:mm")})`
                                    )
                                    .join(", ");

                                return [allDayDisplay, timedDisplay].filter(Boolean).join(", ");
                            },
                        },
                        {
                            title: "Preferred Date(s)",
                            dataIndex: "preferred_dates",
                            render: (dates) => dates?.join(", "),
                        },
                        {title: "Tickets", dataIndex: "number_of_tickets"},
                        {title: "Special Requests", dataIndex: "special_requests"},
                        {
                            title: "Actions",
                            render: (_, record) => (
                                <Space>
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setEditingRequest(record);
                                            form.setFieldsValue({
                                                customer: record.customer,
                                                event: record.event,
                                                preferred_dates: record.preferred_dates,
                                                number_of_tickets: record.number_of_tickets,
                                                special_requests: record.special_requests,
                                            });
                                            setFormVisible(true);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                    <Popconfirm
                                        title="Are you sure?"
                                        onConfirm={() => handleDeleteRequest(record.id)}
                                    >
                                        <Button danger size="small">
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>


            <Modal
                open={formVisible}
                title={editingRequest ? "Edit Ticket Request" : "Request Tickets"}
                onCancel={() => {
                    setFormVisible(false);
                    setEditingRequest(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
            >
                <Form layout="vertical" form={form} onFinish={handleSubmit}>
                    <Form.Item name="customer" label="Customer"
                               rules={[{required: true, message: "Please select a customer"}]}>
                        <Select
                            showSearch
                            filterOption={(input, option: DefaultOptionType | undefined) => {
                                const label = (option?.label as string).toLowerCase();
                                return label.includes(input.toLowerCase());
                            }}
                            options={(Array.isArray(customers) ? customers : []).map((c) => ({
                                value: c.id,
                                label: `${c.first_name} ${c.last_name} (${c.mmid})`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item name="event" label="Event" rules={[{required: true, message: "Please select an event"}]}>
                        <Select>
                            {events.map((e) => (
                                <Option key={e.id} value={e.id}>{e.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="preferred_dates" label="Preferred Date(s)"
                               rules={[{required: true, message: "Please enter preferred dates"}]}>
                        <Select
                            mode="multiple"
                            placeholder="Select one or more preferred dates"
                            options={
                                eventsLookup[selectedEvent]?.event_dates?.map((d: any) => ({
                                    label: dayjs(d.date).format("YYYY-MM-DD"),
                                    value: dayjs(d.date).format("YYYY-MM-DD"),
                                })) || []
                            }
                        />
                    </Form.Item>

                    <Form.Item name="number_of_tickets" label="Number of Tickets"
                               rules={[{required: true, message: "Please enter the number of tickets"}]}>
                        <InputNumber min={1} style={{width: "100%"}}/>
                    </Form.Item>

                    <Form.Item name="special_requests" label="Special Requests">
                        <Input.TextArea rows={3}/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={eventModalVisible}
                title={editingEvent ? "Edit Event" : "Create New Event"}
                onCancel={() => {
                    setEventModalVisible(false);
                    setEditingEvent(null);
                    eventForm.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setEventModalVisible(false);
                        setEditingEvent(null);
                        eventForm.resetFields();
                    }}>
                        Cancel
                    </Button>,
                    editingEvent && (
                        <Button key="delete" danger onClick={async () => {
                            try {
                                await api.delete(`/cc/events/${editingEvent.id}/`);
                                message.success("Event deleted");
                                setEventModalVisible(false);
                                setEditingEvent(null);
                                fetchEvents();
                            } catch {
                                message.error("Failed to delete event");
                            }
                        }}>
                            Delete
                        </Button>
                    ),
                    <Button key="submit" type="primary" onClick={() => eventForm.submit()}>
                        {editingEvent ? "Update" : "Create"}
                    </Button>,
                ]}
            >
                <Form
                    layout="vertical"
                    form={eventForm}
                    onFinish={async (values) => {
                        const payload = {
                            name: values.name,
                            event_dates: values.event_dates,
                            priority: values.priority,
                        };

                        try {
                            if (editingEvent) {
                                await api.patch(`/cc/events/${editingEvent.id}/`, payload);
                                message.success("Event updated");
                            } else {
                                await api.post("/cc/events/", payload);
                                message.success("Event created");
                            }

                            eventForm.resetFields();
                            setEventModalVisible(false);
                            setEditingEvent(null);
                            fetchEvents();
                        } catch {
                            message.error(editingEvent ? "Failed to update event" : "Failed to create event");
                        }
                    }}
                >
                    <Form.Item name="name" label="Event Name" rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label="Event Dates"
                        required
                        shouldUpdate={(prevValues, currentValues) => prevValues.event_dates !== currentValues.event_dates}
                    >
                        <EventDatesFormList/>
                    </Form.Item>
                    <Form.Item name="priority" label="Priority" rules={[{required: true}]}>
                        <Select>
                            {priorityOptions.map((p) => (
                                <Option key={p} value={p}>{p.replace("_", " ").toUpperCase()}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default EntertainmentPage;